// PATs v2: Token summary display behavior

import {observe} from 'selector-observer'
import {on} from 'delegated-events'

function updateElText(selector: string, text: string) {
  const el = document.querySelector<HTMLElement>(selector)!
  if (el) el.textContent = text
}

function elCount(selector: string): number {
  return document.querySelectorAll<HTMLInputElement>(selector).length
}

function updateName() {
  const nameInputEl = document.querySelector<HTMLInputElement>('#user_programmatic_access_name')!
  updateElText('#token-summary [data-token-summary="name"]', nameInputEl.value)
}

function updatePermissionCount(permissionType: string) {
  const permissionCount = elCount(
    `[data-${permissionType}-permission] .js-permissions-selector [id^=integration_permission_]:checked:not([data-permission="none"])`
  )
  updateElText(`#token-summary [data-token-summary="${permissionType}-permission-count"]`, String(permissionCount))
}

function updateRepositoryCount() {
  const repoSelectorRadio = document.querySelector<HTMLInputElement>('.js-installation-repositories-radio:checked')
  const targetSelection = repoSelectorRadio ? repoSelectorRadio.value : 'none'

  if (targetSelection === 'selected') {
    const repositoryCount = elCount('.js-selected-repository-field')
    updateElText('#token-summary [data-token-summary="repository-count"]', String(repositoryCount))
  } else {
    updateElText('#token-summary [data-token-summary="repository-count"]', targetSelection)
  }
}

function updateExpiration() {
  const expirationValue = document.querySelector<HTMLSelectElement>('.js-default-token-expiration-select')!.value
  if (expirationValue === 'none') {
    updateElText('#token-summary [data-token-summary="expiration"]', 'never.')
  } else if (expirationValue === 'custom') {
    const customDate = document.querySelector<HTMLInputElement>('#user_programmatic_access_custom_expires_at')!.value
    // Setting the time to midnight because otherwise the default of midnight UTC might output the prior date based on timezone offset
    const dateString = customDate ? formatDateString(new Date(`${customDate}T00:00:00`)) : 'an unselected custom date'
    updateElText('#token-summary [data-token-summary="expiration"]', `on ${dateString}.`)
  } else {
    const dateString = calculateDate(expirationValue)
    updateElText('#token-summary [data-token-summary="expiration"]', `on ${dateString}.`)
  }
}

function formatDateString(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
    .format(date)
    .replace(/,(?=\s\d{4})/, '')
}

function calculateDate(expirationValue: string): string {
  const date = new Date()
  date.setDate(date.getDate() + Number(expirationValue))
  return formatDateString(date)
}

function toggleRepoPermissionVisibility() {
  const targetSelection = document.querySelector<HTMLInputElement>('.js-installation-repositories-radio:checked')!.value
  const permissionSection = document.querySelector<HTMLElement>(
    '.js-integration-permissions-selector section[data-repository-permission]'
  )!

  if (targetSelection === 'none') {
    permissionSection.hidden = true
    updateElText(`#token-summary [data-token-summary="repository-permission-count"]`, String(0))
  } else {
    permissionSection.hidden = false
    updatePermissionCount('repository')
  }
}

function setInitialValues() {
  updatePermissionCount('repository')
  updatePermissionCount('user')
  updateRepositoryCount()
}

observe('.js-user-programmatic-access-form .js-integrations-install-repo-selection', el => {
  // observes when repository selections are added to the DOM
  // handles the case when user toggles back to select repositories displaying previous selections
  const selectedRepoListObserver = new MutationObserver(updateRepositoryCount)
  // observe subtree from element that is never removed from the dom to catch all changes
  selectedRepoListObserver.observe(el, {childList: true, subtree: true})
})

observe('.js-user-programmatic-access-form', setInitialValues)
on(
  'change',
  '.js-user-programmatic-access-form [data-repository-permission] .js-permissions-selector [id^=integration_permission_]',
  () => updatePermissionCount('repository')
)
on(
  'change',
  '.js-user-programmatic-access-form [data-user-permission] .js-permissions-selector [id^=integration_permission_]',
  () => updatePermissionCount('user')
)
on('change', '.js-user-programmatic-access-form .js-installation-repositories-radio', () => {
  updateRepositoryCount()
  toggleRepoPermissionVisibility()
})
on('change', '.js-user-programmatic-access-form #user_programmatic_access_name', updateName)
on('change', '.js-user-programmatic-access-form .js-default-token-expiration-select', updateExpiration)
on('change', '.js-user-programmatic-access-form #user_programmatic_access_custom_expires_at', updateExpiration)
